import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Check, X, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface PromoCodeInputProps {
  onPromoApplied: (code: string, discount: string) => void;
  onPromoRemoved: () => void;
  userType: 'buyer' | 'seller';
  planType: 'monthly' | 'annual';
}

export const PromoCodeInput = ({ 
  onPromoApplied, 
  onPromoRemoved, 
  userType, 
  planType 
}: PromoCodeInputProps) => {
  const [code, setCode] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [discount, setDiscount] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    if (!code.trim()) {
      setError("Please enter a promo code");
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('validate-promo-code', {
        body: { 
          code: code.trim(), 
          user_type: userType, 
          plan_type: planType 
        }
      });

      if (functionError) throw functionError;

      if (data?.valid) {
        setAppliedCode(code.trim().toUpperCase());
        setDiscount(data.discount);
        onPromoApplied(code.trim().toUpperCase(), data.discount);
        toast({
          title: "Promo code applied!",
          description: `You'll save ${data.discount} on your subscription`,
        });
      } else {
        setError(data?.error || "Invalid promo code");
      }
    } catch (err: any) {
      console.error("Promo code validation error:", err);
      setError("We couldn't validate this code. Please check it and try again.");
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemove = () => {
    setAppliedCode(null);
    setDiscount(null);
    setCode("");
    setError(null);
    onPromoRemoved();
    toast({
      title: "Promo code removed",
      description: "The discount has been removed from your order",
    });
  };

  if (appliedCode && discount) {
    return (
      <Alert className="bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800">
        <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
        <AlertDescription className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-green-800 dark:text-green-200">
              Promo code applied: {appliedCode}
            </p>
            <p className="text-sm text-green-600 dark:text-green-400">
              You're saving {discount}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="promo-code" className="flex items-center gap-2">
        <Tag className="h-4 w-4" />
        Promo Code (Optional)
      </Label>
      <div className="flex gap-2">
        <Input
          id="promo-code"
          placeholder="Enter promo code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleApply();
            }
          }}
          disabled={isValidating}
          className={error ? "border-red-500" : ""}
        />
        <Button
          onClick={handleApply}
          disabled={isValidating || !code.trim()}
          variant="outline"
        >
          {isValidating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Validating...
            </>
          ) : (
            "Apply"
          )}
        </Button>
      </div>
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};
